import videojs from 'video.js';
import Hls from 'hls.js';
import './audiotracks-button.js';
import './audiotracks-settings.js';
import AudiotracksListFallback from './audiotracks-list.fallback.js';
import AudiotracksList from './audiotracks-list.js';
import { checkTouchScreen } from "../../js/helpers/checkTouchScreen";

const registerPlugin = videojs.registerPlugin || videojs.plugin;

// Default options for the plugin.
const defaults = {
	isAvailable: true,
	restrictedTrackIds: [],
	requestAccess: () => {}
};

/**
 * @typedef {Object} AudioTrack
 * @property {string} type - The type of the track
 * @property {string} url - The URL of the track
 * @property {string} title - The title of the track
 * @property {string} id - The unique identifier of the track
 */

/**
 * @typedef {Object} Options
 * @property {Array<AudioTrack>} tracks - The list of alternative audio tracks with same video for the broadcast
 * @property {string} title
 * @property {boolean} isAvailable
 * @property {boolean} isMatchTVMode
 * @property {Array<Number>} restrictedTrackIds
 */

/**
 * @function init
 * @param {Player} player
 * @param {Options} options - An object containing options for the audiotracks plugin
 * @param {Plugin} plugin - The instance of the Plugin class
 */
const init = (player, options, plugin) => {
	player.addClass('vjs-audiotracks');
	let control_bar = player.getChild('controlBar');

	//если кнопка аудиодорожек и список дорожек уже отрисованы, удаляем их,
	//чтобы далее добавить с актуальными данными
	if (control_bar.getChild('audiotracksButton') && player.getChild('audiotracksSettings')) {
		control_bar.removeChild('audiotracksButton');
		player.removeChild('audiotracksSettings');
	}

	let buttonToggle;
	if (control_bar) {
		let custom_control_spacer = control_bar.getChild('customControlSpacer');
		let index = custom_control_spacer && control_bar.children_.indexOf(custom_control_spacer) + 1;
		buttonToggle = control_bar.addChild(
			'audiotracksButton',
			videojs.mergeOptions(options, {
				showRestrictionModalDialog: plugin.showRestrictionModalDialog.bind(plugin)
			}),
			index
		);
	}

	if (options.isMatchTVMode && !options.isAvailable) {
		buttonToggle.addClass('vjs-audiotracks-control_restricted');
	}

	if (options.isMatchTVMode && options.isAvailable) {
		let audiotracksSettings = buttonToggle.addChild('audiotracksSettings', videojs.mergeOptions(options, {
			buttonToggle,
			isTrackRestricted: plugin.isTrackRestricted.bind(plugin),
			showRestrictionModalDialog: plugin.showRestrictionModalDialog.bind(plugin)
		}));

		player.on(checkTouchScreen() ? 'touchstart' : 'click', (event) => {
			if (event.target.closest('.vjs-audiotracks-control, .vjs-audiotracks-settings')) return;
			if (!audiotracksSettings.hasClass('vjs-hidden')) {
				buttonToggle.hideSettings();
			}
		});
	} else {
		player.addChild('audiotracksSettings', options);
	}

	plugin.trigger('ready');
};

/**
 * @function initFallback
 * @param {Object} player - The main video.js player instance
 * @param {Options} options - An object containing options for the audiotracks plugin
 * @param {Plugin} plugin - The instance of the Plugin class
 */
const initFallback = (player, opts, plugin) => {
	opts.audiotracksList = new AudiotracksListFallback(player, opts);
	init(player, videojs.mergeOptions(defaults, opts), plugin);
};

/**
 * The Audiotracks class represents an audiotracks plugin for the video.js player.
 *
 * @param {Object} player - The main video.js player instance
 * @param {Options} options - An object containing options for the audiotracks plugin
 */
class Audiotracks extends videojs.getPlugin('plugin') {
	constructor(player, options = {}) {
		super(player);

		this.isPlayerReady = false;
		this.handleHlsAudioTracksUpdated = this.handleHlsAudioTracksUpdated.bind(this);
		this.disposeHandlers = [];
		this.disposeTracksInSettingsGroup = null;

		/* this.initialOptions используется в videojs-multicam
		 * для переинициализации videojs-audiotracks новыми
		 * звуковыми дорожками из новой трансляции.
		 */
		this.initialOptions = options;
		this.options = videojs.mergeOptions({}, defaults, options);
		this.options.isMatchTVMode = this.options.mode === 'matchtv';

		this.setListeners();
	}

	setListeners() {
		const addSettingsToSettingsGroupByCondition = () => {
			if (!this.player.usingPlugin('settingsGroup')) return;
			if (this.player.settingsGroup().isReady) {
				this.addSettingsToSettingsGroup();
			} else {
				this.player.settingsGroup().on(
					'settingsGroup/ready',
					this.addSettingsToSettingsGroup.bind(this)
				);
			}
		};

		this.player.ready(() => {
			if (Hls.isSupported()) {
				this.isPlayerReady = true;
				return;
			}
			initFallback(this.player, this.options, this);
			addSettingsToSettingsGroupByCondition();
		});

		const updateHlsAudioTracks = () => {
			this.handleHlsAudioTracksUpdated();
			addSettingsToSettingsGroupByCondition();
		}

		// Событие 'Html5HlsJS/ready' создаётся только если Hls.isSupported() возвращает true.
		this.player.one('Html5HlsJS/ready', () => {
			//функция ниже необходима, так как если
			//в видео стандартные аудиодорожки, добавленные через админку,
			//не происходит событие  hlsAudioTracksUpdated,
			//и компоненты audiotracksButton и audiotracksSettings не отрисовываются вообще
			updateHlsAudioTracks();

			// Вызывается только если измениилсь дорожки. Не вызывается если выбрать дорожку.
			this.player.hls_.on('hlsAudioTracksUpdated', () => {
				updateHlsAudioTracks();
			});
		});
	}

	addSettingsToSettingsGroup() {
		if (this.options.audiotracksList.items.length < 2) {
			return;
		}
		const audiotracksButton = this.player.getDescendant(['controlBar', 'audiotracksButton']);
		const {
			update: updateTracksInSettingsGroup,
			dispose: disposeTracksInSettingsGroup
		} = this.player.settingsGroup().addSettingsSection({
			name: {
				text: 'Комментатор',
				className: !this.options.isAvailable && ((defaultClassName) => `${defaultClassName}_restricted`),
				onClick: !this.options.isAvailable && (() => {
					this.showRestrictionModalDialog(() => {
						this.options.requestAccess({accessTo: 'feature'});
					});
				})
			},
			componentName: 'AudiotracksButton',
			id: 'audiotracks',
			getCurrentValue: () => '',
			items: this.options.audiotracksList.items.map((track) => ({
				name: track.title,
				checkSelected: () => track.selected,
				checkDisabled: () => false,
				onClick: () => {
					if (this.isTrackRestricted(track.id)) {
						this.showRestrictionModalDialog(() => {
							this.options.requestAccess({accessTo: 'quality', quality: track.id});
						});
						return;
					}
					this.options.audiotracksList.play(track.id);
					if (!audiotracksButton) return;
					audiotracksButton.toggleClass('vjs-audiotracks-control_cross-out', track.id === 'a_noise');
				},
				className: this.isTrackRestricted(track.id) && ((defaultClassName) => `${defaultClassName}_restricted`)
			}))
		});
		// update вызывает dispose не у того компонента.
		this.player.on('Audiotracks/changing-track', updateTracksInSettingsGroup);
		this.disposeHandlers.push(
			disposeTracksInSettingsGroup,
			() => this.player.off('Audiotracks/changing-track', updateTracksInSettingsGroup)
		);
	}

	removeListeners() {
		if (!this.player.hls_) return;
		this.player.hls_.off('hlsAudioTracksUpdated', this.handleHlsAudioTracksUpdated);
	}

	handleHlsAudioTracksUpdated() {
		let audiotracksList = this.player.tech(false).audioTracks();
		if (!audiotracksList) return;

		let labels = new Set();
		for (let i = 0; i < audiotracksList.length; i++) {
			labels.add(audiotracksList[i].label);
		}
		if (labels.size <= 1) {
			let afterPlayerReady = () => {
				if (!this.isPlayerReady) {
					setTimeout(afterPlayerReady, 50);
					return;
				}
				initFallback(this.player, this.options, this);
			};
			afterPlayerReady();
			return;
		}

		this.options.tracks = Array.from(labels)
			.map((label) => ({
				id: label,
				title: label
			}));
		this.options.audiotracksList = new AudiotracksList(this.player, this.options);

		let audioTrackButton = this.player.getDescendant(['controlBar', 'AudioTrackButton']);
		if (audioTrackButton) {
			audioTrackButton.hide();
		}

		let afterPlayerReady = () => {
			if (!this.isPlayerReady) {
				setTimeout(afterPlayerReady, 50);
				return;
			}

			init(this.player, videojs.mergeOptions(defaults, this.options), this);

			let buttonToggle = this.player.getDescendant(['controlBar', 'audiotracksButton']);
			if (buttonToggle) {
				buttonToggle.show();
			}
		};
		afterPlayerReady();
	}

	showRestrictionModalDialog(getAccessCallback) {
		const restrictionModalDialog = this.player.getChild('restrictionModalDialog');
		if (!restrictionModalDialog) return;
		restrictionModalDialog.toggleDisplay();
		restrictionModalDialog.onGetAccess(() => getAccessCallback());
		const buttonToggle = this.player.getDescendant(['controlBar', 'audiotracksButton']);
		if (buttonToggle) {
			buttonToggle.hideSettings();
		}
	}

	removeChildren() {
		let controlBar = this.player.getChild('controlBar');
		let audiotracksButton = controlBar && controlBar.getChild('audiotracksButton');
		if (audiotracksButton) {
			controlBar.removeChild(audiotracksButton);
		}
	}

	dispose() {
		this.removeListeners();
		this.removeChildren();
		this.disposeHandlers.forEach((handler) => handler());
		super.dispose();
	}

	resetSavedPlayerState() {
		this.setState({
			playerDataBeforeChangeSrc: Audiotracks.defaultState.playerDataBeforeChangeSrc
		});
	}

	isTrackRestricted(trackId) {
		return this.options.restrictedTrackIds.includes(trackId);
	}
}

// Register the plugin with video.js.
registerPlugin('audiotracks', Audiotracks);

// Include the version number.
Audiotracks.VERSION = '__VERSION__';

Audiotracks.defaultState = {
	playerDataBeforeChangeSrc: {
		currentTime: null,
		currentType: null,
		volume: null,
		paused: null
	}
};

export default Audiotracks;
